import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  settingsService,
  type BankAccount,
  type CreateBankAccountInput,
  type UpdateBankAccountInput,
} from '@/services/settings.service';
import { settingsKeys } from '@/services/queryKeys';
import { mockBankAccounts } from '@/mocks/tenantSettings';
import { isArrayResponse, withMockFallback } from '@/utils/queryMock';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useBankAccounts() {
  return useQuery<BankAccount[]>({
    queryKey: settingsKeys.bankAccounts(),
    queryFn: () =>
      withMockFallback(
        () => settingsService.listBankAccounts(),
        async () => {
          await sleep(120);
          return mockBankAccounts;
        },
        { label: 'settings.bankAccounts', validate: isArrayResponse },
      ),
    retry: 0,
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBankAccountInput) =>
      withMockFallback(
        () => settingsService.createBankAccount(body),
        async () =>
          ({
            _id: `mock-${Date.now()}`,
            tenant_id: 'mock-tenant',
            ...body,
            is_default: !!body.is_default,
          }) as BankAccount,
        { label: 'settings.createBankAccount' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.bankAccounts() }),
  });
}

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateBankAccountInput;
    }) =>
      withMockFallback(
        () => settingsService.updateBankAccount(id, body),
        async () => ({ _id: id, ...body } as unknown as BankAccount),
        { label: 'settings.updateBankAccount' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.bankAccounts() }),
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      withMockFallback(
        () => settingsService.deleteBankAccount(id),
        async () => ({ deleted: true }),
        { label: 'settings.deleteBankAccount' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.bankAccounts() }),
  });
}

export function useSetDefaultBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      withMockFallback(
        () => settingsService.setDefaultBankAccount(id),
        async () => ({ _id: id, is_default: true } as unknown as BankAccount),
        { label: 'settings.setDefaultBankAccount' },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: settingsKeys.bankAccounts() }),
  });
}
